import React, { useEffect, useRef } from 'react';
import HomeHeader from '@/components/HomeHeader';
import Footer from '@/components/homepage/Footer';
import { usePageMeta } from '@/hooks/usePageMeta';
import TableOfContents from '@/components/resources/TableOfContents';
import EmailCapture from '@/components/resources/EmailCapture';

const TITLE = 'What to Do When a Major Donor Stops Giving';
const DESCRIPTION =
    "A practical guide for missionaries when a large recurring donor stops giving — how to confirm what happened, the four most common reasons, how to respond to each, and how to avoid depending on one gift again.";

const JSON_LD = {
    '@context': 'https://schema.org',
    '@graph': [
        {
            '@type': 'Article',
            headline: TITLE,
            description: DESCRIPTION,
            image: 'https://devotionfund.com/resources/cover16.jpg',
            datePublished: '2026-07-29',
            dateModified: '2026-07-29',
            author: { '@type': 'Organization', name: 'Devotion Fund', url: 'https://devotionfund.com' },
            publisher: {
                '@type': 'Organization',
                name: 'Devotion Fund',
                logo: { '@type': 'ImageObject', url: 'https://devotionfund.com/logo.png' },
            },
            mainEntityOfPage: {
                '@id': 'https://devotionfund.com/resources/what-to-do-when-a-major-donor-stops-giving',
                '@type': 'WebPage',
            },
        },
        {
            '@type': 'FAQPage',
            mainEntity: [
                {
                    '@type': 'Question',
                    name: 'Should I ask a major donor directly why they stopped giving?',
                    acceptedAnswer: {
                        '@type': 'Answer',
                        text: "Yes, once you've confirmed it wasn't just a failed card charge. A brief, warm, no-pressure check-in is almost always better than guessing — most donors appreciate being asked directly rather than left to wonder if you noticed.",
                    },
                },
                {
                    '@type': 'Question',
                    name: 'How long should I wait before reaching out after a missed gift?',
                    acceptedAnswer: {
                        '@type': 'Answer',
                        text: "For a monthly donor, one missed gift is often just a timing or card issue — wait for a second missed month before reaching out about the pattern itself, but do send a normal, friendly update in between regardless.",
                    },
                },
                {
                    '@type': 'Question',
                    name: 'Is it appropriate to ask a lapsed donor to come back?',
                    acceptedAnswer: {
                        '@type': 'Answer',
                        text: "Yes, especially after addressing whatever caused them to stop. A specific, low-pressure re-invitation months later — 'would you consider restarting at any level that works' — often succeeds where a guilt-based appeal would fail.",
                    },
                },
                {
                    '@type': 'Question',
                    name: 'How do I protect my budget from relying on one major donor?',
                    acceptedAnswer: {
                        '@type': 'Answer',
                        text: "Cap any single donor at a reasonable share of your total support — many missionaries use roughly 10-15% as a ceiling — and treat growing the number of mid-size recurring donors as an ongoing goal, not a one-time project.",
                    },
                },
            ],
        },
    ],
};

export default function WhatToDoWhenAMajorDonorStopsGiving() {
    usePageMeta(TITLE, DESCRIPTION, { path: '/resources/what-to-do-when-a-major-donor-stops-giving' });

    const articleRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const el = document.createElement('script');
        el.type = 'application/ld+json';
        el.setAttribute('data-major-donor-stops-jsonld', 'true');
        el.textContent = JSON.stringify(JSON_LD);
        document.head.appendChild(el);
        return () => {
            el.remove();
        };
    }, []);

    return (
        <div className="min-h-screen bg-white flex flex-col">
            <HomeHeader />
            <div className="w-full h-64 md:h-80 bg-gray-100 overflow-hidden">
                <img src="/resources/cover16.jpg" alt="A missionary reviewing their support budget after a major donor stopped giving" className="w-full h-full object-cover" />
            </div>
            <div className="max-w-3xl mx-auto px-4 py-12 w-full">
                <h1 className="text-3xl font-bold mb-6">{TITLE}</h1>
                <TableOfContents targetRef={articleRef} />
                <div className="prose max-w-none" ref={articleRef}>
                    <p>Losing a large recurring donor hits differently than a small one lapsing quietly. When someone who covers 10, 15, or even 20% of your monthly support stops giving, it can feel like the whole budget just cracked. The instinct is to panic or to avoid the topic entirely — neither helps. There's a calmer, more effective sequence: confirm what actually happened, respond to the real reason, and use the moment to reduce how exposed you are to any single gift going forward.</p>

                    <h2>First, confirm what actually happened</h2>

                    <p>Before assuming a donor has decided to stop, check whether this is a payment problem rather than a decision. Expired cards, replaced cards after fraud, and bank account changes cause a large share of "missed" recurring gifts — the donor often doesn't even know the charge failed. Check your platform's failed-payment notifications first; if it's a card issue, a short "hey, looks like your card on file may have expired — no rush, just wanted to flag it" resolves most of these without ever touching the harder conversation below.</p>

                    <h2>Don't guess — ask directly, but gently</h2>

                    <p>If the gift genuinely stopped rather than failed, resist the urge to speculate silently for weeks. A brief, warm check-in — with zero guilt-tripping — usually gets a real answer faster than waiting and wondering: "Hi [name], I noticed your gift didn't come through this month and wanted to check in — no pressure at all, just wanted to make sure everything's okay on your end." Most donors would rather be asked directly than sense you've noticed and said nothing.</p>

                    <h2>The four most common reasons a major donor stops</h2>

                    <p><strong>Financial change.</strong> A job loss, a large unexpected expense, or a shift in their own household budget. This is the most common reason and has nothing to do with you or your ministry.</p>

                    <p><strong>Disengagement.</strong> They've quietly lost the sense of connection to your work — often because updates became infrequent or generic rather than specific and personal.</p>

                    <p><strong>An unaddressed concern.</strong> Something about how support is used, a question they never felt comfortable raising, or a change in your ministry direction they weren't sure about.</p>

                    <p><strong>A natural life change.</strong> Retirement, a move, a shift in their own giving priorities, or simply a season where their capacity changed. Not every lapsed gift is fixable, and that's normal.</p>

                    <h2>How to respond to each reason</h2>

                    <p>For a financial change, respond with genuine grace and no pressure to return — offer to keep them on your update list at no cost to them, since many financially-strained donors quietly return once their situation improves. For disengagement, the fix is usually upstream: tighten your update rhythm and specificity going forward, following the same approach in <a href="/resources/donor-retention-for-missionaries">donor retention for missionaries</a>, rather than trying to win back this one relationship with a single great message. For an unaddressed concern, ask an open question ("is there anything about the ministry or how support is used that you'd want to know more about?") rather than assuming — most concerns are resolvable once they're actually named. For a natural life change, a warm thank-you for their past support and an open door, without an active ask, respects where they are.</p>

                    <h2>What NOT to do: guilt, silence, or immediately replacing them</h2>

                    <p>Three responses reliably make this worse. Guilt-based language about your budget shortfall pressures a donor into a resentful yes or, more often, permanently ends the relationship. Complete silence — never acknowledging the missed gift at all — reads as either not noticing or not caring, both of which erode trust. And rushing to publicly announce a "support gap" or replace the loss with a dramatic appeal to everyone else can look reactive and make remaining donors uneasy about your financial stability. A calm, individual, low-key response protects both the relationship and your credibility with everyone else watching.</p>

                    <h2>Rebuilding without becoming dependent on one donor again</h2>

                    <p>A major donor lapsing is a useful forcing function to look at how concentrated your support base actually is. Many experienced missionaries cap any single donor at roughly 10-15% of total monthly support as an informal ceiling, specifically so one relationship change can't destabilize the whole budget. If this gap exposed a concentration problem, treat rebuilding toward more mid-size recurring donors as an ongoing habit rather than a one-time scramble — the same phased approach in <a href="/resources/raise-support-fast-30-60-90-day-plan">raising support fast with a 30/60/90-day plan</a> works just as well for backfilling a gap as it does for starting from zero.</p>

                    <h2>Frequently asked questions</h2>

                    <p><strong>Should I ask a major donor directly why they stopped giving?</strong><br />
                    Yes, once you've ruled out a failed payment — a brief, warm check-in beats guessing.</p>

                    <p><strong>How long should I wait before reaching out after a missed gift?</strong><br />
                    One missed month is often just a timing issue; a second missed month is worth a direct check-in.</p>

                    <p><strong>Is it appropriate to ask a lapsed donor to come back?</strong><br />
                    Yes, especially months later with a low-pressure, specific re-invitation.</p>

                    <p><strong>How do I protect my budget from relying on one major donor?</strong><br />
                    Cap any single donor at roughly 10-15% of total support and keep growing your mid-size donor base.</p>

                    <h2>Next reads on Devotion Fund</h2>
                    <ul>
                        <li><a href="/resources/donor-retention-for-missionaries">Donor Retention for Missionaries: How to Stop Supporters From Quietly Lapsing</a></li>
                        <li><a href="/resources/second-term-missionary-support-raising">Second-Term Missionary Support Raising: What Changes the Second Time Around</a></li>
                        <li><a href="/resources/how-to-write-a-missionary-thank-you-letter">How to Write a Missionary Thank-You Letter Donors Actually Remember</a></li>
                    </ul>

                    <h2>About the author</h2>
                    <p>We're the team behind <a href="/">Devotion Fund</a>. We built this from patterns we've seen across missionaries who leaned on one generous donor for years and had to relearn support raising fast the day that gift stopped.</p>
                    <p><small><em>Last updated: 2026-07-29.</em></small></p>
                </div>
                <div className="mt-8">
                    <EmailCapture page="/resources/what-to-do-when-a-major-donor-stops-giving" />
                </div>
            </div>
            <Footer />
        </div>
    );
}
